import {
  Checkbox,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Label,
  MessageBar,
  MessageBarType,
  Stack,
  Text,
} from "@fluentui/react";
import { toast } from "components/FluentToast";
import { FormSpinner } from "components/FormSpinner";
import { Field } from "components/form/Field";
import { ErrorMessage, StyledPrimaryButton } from "components/styled";
import { formatISO } from "date-fns/esm";
import { Form, Formik } from "formik";
import { useSetVehiclePremiumPolicyData } from "hooks/data/mutations/useSetVehiclePremiumPolicyData";
import usePhraseActivationStatus from "hooks/usePhraseActivationStatus";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  closeSetPolicyNumberAndDocumentDateDialog,
  loadVehiclesActions,
} from "store/fleetPage";
import { useSelector } from "store/hooks";
import { PremiumType } from "types/types";
import { setYupLocale } from "utils/setYupLocale";
import * as yup from "yup";
import { getPolicyDataLines } from "./setPolicyNumberAndDocumentDateUtils";

type FormValues = {
  insurerPolicyNumber: string;
  documentDate: Date | null;
  premiumTypes: PremiumType[];
};

const toISODate = (date: Date) => formatISO(date, { representation: "date" });

// sets the policy number and the document date of the current premiums of the selected main lines of a vehicle
// (the policies of these lines are marked as issued)
export const SetPolicyNumberAndDocumentDateDialog = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [didTrySubmit, setDidTrySubmit] = useState(false);
  const phraseActivated = usePhraseActivationStatus().activated;

  const fleetId = useSelector(
    (s) => s.fleetPage.setPolicyNumberAndDocumentDateDialog.fleetId,
  );
  const vehicle = useSelector(
    (s) => s.fleetPage.setPolicyNumberAndDocumentDateDialog.vehicle,
  );
  const fleetAppSettings = useSelector((s) => s.fleetForm.fleetAppSettings);
  const taxonomy = useSelector((s) => s.taxonomy);

  const lines = useMemo(
    () => getPolicyDataLines(fleetAppSettings, vehicle),
    [fleetAppSettings, vehicle],
  );

  const { mutate: onSetVehiclePremiumPolicyData, isLoading } =
    useSetVehiclePremiumPolicyData();

  const close = () => {
    dispatch(closeSetPolicyNumberAndDocumentDateDialog());
  };

  const schema = useMemo(() => {
    setYupLocale(t);
    return yup.object({
      insurerPolicyNumber: yup.string().trim().max(34).required().nullable(),
      documentDate: yup.mixed().required(),
      premiumTypes: yup
        .array()
        .min(
          1,
          t("bfm.setPolicyNumberAndDocumentDate.validation.atLeastOneLine"),
        ),
    });
  }, [t]);

  const initialValues: FormValues = useMemo(
    () => ({
      insurerPolicyNumber: "",
      documentDate: null,
      // preselect the line when the vehicle has only one
      premiumTypes: lines.length === 1 ? [lines[0].premiumType] : [],
    }),
    [lines],
  );

  const onSubmit = (values: FormValues) => {
    onSetVehiclePremiumPolicyData(
      {
        fleetId: "" + (fleetId ?? vehicle?.fleetId),
        vehicleId: "" + vehicle?.vehicleId,
        body: {
          insurerPolicyNumber: values.insurerPolicyNumber.trim(),
          documentDate: toISODate(values.documentDate as Date),
          premiumTypes: lines
            .filter((line) => values.premiumTypes.includes(line.premiumType))
            .map((line) => taxonomy.PremiumType.byCode[line.premiumType]?.id),
          comment: "",
        },
      },
      {
        onSuccess: () => {
          dispatch(loadVehiclesActions.trigger({ isReset: true }));
          toast.success(t("bfm.success"));
          close();
        },
      },
    );
  };

  return (
    <Dialog
      hidden={false}
      onDismiss={close}
      dialogContentProps={{
        type: DialogType.normal,
        title: t("bfm.setPolicyNumberAndDocumentDate.label"),
        subText: vehicle?.licensePlate || vehicle?.vin || undefined,
        showCloseButton: true,
      }}
      minWidth={450}
      modalProps={{
        isBlocking: phraseActivated ? false : true,
      }}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize
        validationSchema={schema}
        validateOnChange={didTrySubmit}
        validateOnBlur={didTrySubmit}
      >
        {({ submitForm, values, errors, setFieldValue }) => {
          return (
            <Form>
              <Field
                name="insurerPolicyNumber"
                type="text"
                label={"bfm.vehicleForm.policyNumber.label"}
                isRequired
              />
              <Field
                name="documentDate"
                type="date"
                label={"bfm.vehicleForm.documentDate.label"}
                isRequired
                d={({ values, errors, setFieldValue }) => ({
                  values,
                  errors,
                  setFieldValue,
                  defaultValue: values?.documentDate,
                })}
              />
              <Label required>
                {t("bfm.setPolicyNumberAndDocumentDate.lines.label")}
              </Label>
              <Stack tokens={{ childrenGap: 8 }}>
                {lines.map((line) => {
                  const currentPolicyNumber = vehicle?.[
                    line.policyNumberField
                  ] as string | null | undefined;
                  return (
                    <Stack
                      key={line.premiumType}
                      horizontal
                      verticalAlign="center"
                      tokens={{ childrenGap: 8 }}
                    >
                      <Checkbox
                        label={t(line.premiumType)}
                        checked={values.premiumTypes.includes(line.premiumType)}
                        onChange={(e, checked) => {
                          setFieldValue(
                            "premiumTypes",
                            checked
                              ? [...values.premiumTypes, line.premiumType]
                              : values.premiumTypes.filter(
                                  (pt) => pt !== line.premiumType,
                                ),
                          );
                        }}
                      />
                      {currentPolicyNumber ? (
                        <Text variant="small">
                          ({t("bfm.vehicleForm.policyNumber.label")}:{" "}
                          {currentPolicyNumber})
                        </Text>
                      ) : null}
                    </Stack>
                  );
                })}
              </Stack>
              {errors.premiumTypes && (
                <ErrorMessage id="premiumTypes_error">
                  {errors.premiumTypes as string}
                </ErrorMessage>
              )}
              <MessageBar
                messageBarType={MessageBarType.info}
                styles={{ root: { marginTop: 15 } }}
              >
                {t("bfm.setPolicyNumberAndDocumentDate.info")}
              </MessageBar>
              <DialogFooter>
                <StyledPrimaryButton
                  onClick={() => {
                    setDidTrySubmit(true);
                    submitForm();
                  }}
                  text={t("greco.save")}
                  iconProps={{
                    iconName: "Save",
                  }}
                  disabled={isLoading}
                  style={{
                    marginRight: "5px",
                  }}
                />
                <DefaultButton onClick={close} text={t("bfm.close.label")} />
              </DialogFooter>
              {isLoading && <FormSpinner />}
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};
